import React from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import { selectCurrentLocation } from '../../../store/map/map.selector';

import downloadData from '../../../utilities/downloadData';

import {
  selectAttributeInfo,
  selectDateRange,
  selectUseAlternateData
} from '../../../store/options/options.selector';
import { selectAltHurricanes, selectHurricaneCsvData } from '../../../store/alternate-data/alternate-data.selector';
import { getHurricaneAdvisoriesAsync } from '../../../store/alternate-data/alternate-data.slice';
import { getEpisodesDataAsync } from '../../../store/episodes/episodes.slice';
import { changeEpisodeTimelineVisible } from '../../../store/ui/ui.slice';

export default function HazardChart({
  HighchartsReact,
  Highcharts,
  defaultOptions,
}) {
  const dispatch = useDispatch();
  const data = useSelector(selectAltHurricanes);
  const csvData = useSelector(selectHurricaneCsvData);
  const loc = useSelector(selectCurrentLocation);
  const attrInfo = useSelector(selectAttributeInfo);
  const dateRange = useSelector(selectDateRange);
  const useAlternateData = useSelector(selectUseAlternateData);
  const years = Array.from(
    { length: dateRange.end - dateRange.start + 1 },
    (v, i) => i + dateRange.start
  );

  const name = `Total ${attrInfo.name}s`;

  const options = {
    ...defaultOptions,
    exporting: {
      ...defaultOptions.exporting,
      chartOptions: {
        subtitle: '',
      },
      menuItemDefinitions: {
        downloadData: {
          onclick: () => downloadData(
            'hurricane',
            loc,
            attrInfo,
            csvData,
            dateRange
          ),
          text: 'Download Chart Data',
        },
      },
    },
    legend: { enabled: false },
    chart: {
      ...defaultOptions,
      type: 'column',
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    title: {
      ...defaultOptions.title,
      text: `Annual Reported ${attrInfo.name} Events within 60 nm of ${
        loc.state ? `${loc.name} County, ${loc.state}` : loc.name
      } from ${dateRange.start} to ${dateRange.end}`,
    },
    subtitle: {
      text: 'Click bars for more details',
    },
    series: [
      {
        data: data
          .slice(dateRange.start - 1842, dateRange.end - 1842 + 1)
          .map((stormArr) => stormArr.length),
        name: name,
      },
    ],
    xAxis: {
      ...defaultOptions.xAxis,
      categories: years,
    },
    yAxis: {
      allowDecimals: false,
      endOnTick: false,
      tickAmount: 5,
      gridLineColor: '#a1a1a1',
      type: 'linear',
      title: {
        text: name,
      },
    },
    tooltip: {
      pointFormatter: function () {
        return `${name}: ${this.y}`;
      },
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        events: {
          click: async function (event) {
            if (useAlternateData) {
              dispatch(getHurricaneAdvisoriesAsync(data[event.point.x]));
            } else {
              dispatch(
                getEpisodesDataAsync({
                  type: 'changeYear',
                  payload: [event.point.x + 1842, []],
                })
              );
            }
            dispatch(changeEpisodeTimelineVisible(true));
          },
        },
      },
      column: {
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 0.5,
      },
    },
    navigation: {
      ...defaultOptions.navigation,
      buttonOptions: {
        ...defaultOptions.navigation.buttonOptions,
        y: -5,
      },
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', width: '100%' } }}
    />
  );
}

HazardChart.propTypes = {
  HighchartsReact: PropTypes.object,
  Highcharts: PropTypes.object,
  defaultOptions: PropTypes.object,
};